import { ColumnDef } from "@tanstack/react-table";
import { DropdownWithStatistic } from "components";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { ItemsTable, Loader, Stat, TableItemStatus } from "ui";

import { AdCampaignPerformance, AdPerformanceResponse } from "../../../apis";
import { AdCampaignStatus } from "../../../constants";
import { SectionCard } from "../atoms";
import { formatXof } from "../financialFormat";

import styles from "./AdvertisingSection.module.scss";

interface AdvertisingSectionProps {
  data: AdPerformanceResponse | undefined;
}

const AD_CAMPAIGN_STATUSES_LIST = [
  {
    label: "Active",
    value: AdCampaignStatus.Active,
    backgroundColor: "#16C350"
  },
  {
    label: "Paused",
    value: AdCampaignStatus.Paused,
    backgroundColor: "#FF9900"
  },
  { label: "Ended", value: AdCampaignStatus.Ended, backgroundColor: "#A8A8BB" }
];

const AdvertisingSection = ({ data }: AdvertisingSectionProps) => {
  const { t } = useTranslation();

  const columns: Array<ColumnDef<AdCampaignPerformance, string>> = [
    {
      id: "name",
      header: t("Ad"),
      size: 30,
      cell: ({ row }) => {
        const campaign = row.original;
        const noSales = campaign.attributedUnits === 0;

        return (
          <div>
            <div className={styles.campaignName}>
              <span>{campaign.name}</span>
              <TableItemStatus
                statusesList={AD_CAMPAIGN_STATUSES_LIST}
                activeStatusIndex={AD_CAMPAIGN_STATUSES_LIST.findIndex(
                  (status) => status.value === campaign.status
                )}
              />
              {noSales && (
                <span className={styles.noSalesTag}>{t("No sales")}</span>
              )}
            </div>
            <div className={styles.campaignMeta}>
              {t("Launched on")}{" "}
              {moment(campaign.startedAt).format("MMM DD, YYYY")}
              {campaign.stoppedAt &&
                ` · ${t("stopped on")} ${moment(campaign.stoppedAt).format("MMM DD, YYYY")}`}{" "}
              · {campaign.attributedUnits} {t("attributed units")} · {t("CPA")}{" "}
              {campaign.costPerUnit === null
                ? "—"
                : formatXof(campaign.costPerUnit)}
            </div>
          </div>
        );
      }
    },
    {
      id: "spend",
      header: t("Period spend"),
      size: 13,
      cell: ({ row }) => formatXof(row.original.spend)
    },
    {
      id: "lifetimeSpend",
      header: t("Since launch"),
      size: 12,
      cell: ({ row }) => formatXof(row.original.lifetimeSpend)
    },
    {
      id: "attributedRevenue",
      header: t("Revenue"),
      size: 14,
      cell: ({ row }) => formatXof(row.original.attributedRevenue)
    },
    {
      id: "attributedProfit",
      header: t("Profit"),
      size: 14,
      cell: ({ row }) => formatXof(row.original.attributedProfit)
    },
    {
      id: "roas",
      header: t("ROAS"),
      size: 8,
      cell: ({ row }) => {
        const roas = row.original.roas;
        return (
          <span className={roas ? styles.roasPositive : styles.roasZero}>
            {roas === null ? "—" : `${roas.toFixed(2)}×`}
          </span>
        );
      }
    }
  ];

  if (!data) {
    return (
      <SectionCard title="Advertising">
        <Loader />
      </SectionCard>
    );
  }

  return (
    <SectionCard title="Advertising">
      <div className={styles.topBlock}>
        <Stat
          size="lg"
          title="Ad spend for the period"
          amount={formatXof(data.totalSpend)}
        />
        <Stat
          size="sm"
          title="Attributed revenue"
          amount={formatXof(data.attributedRevenue)}
        />
        <Stat
          size="sm"
          title="Profit generated"
          amount={formatXof(data.attributedProfit)}
        />
        <Stat
          size="sm"
          title="Overall ROAS"
          amount={data.roas === null ? "—" : `${data.roas.toFixed(2)}×`}
        />
      </div>

      <DropdownWithStatistic
        label={`${t("Ads delivered during the period")} · ${data.campaigns.length}`}
        defaultOpen
      >
        <div className={styles.tableWrapper}>
          <div className={styles.attributionNote}>
            {t("Attribution window: 7 days post-click")}
          </div>

          <ItemsTable
            columns={columns}
            data={data.campaigns}
            emptyListMessage="There is no data for selected period"
          />
        </div>
      </DropdownWithStatistic>
    </SectionCard>
  );
};

export default AdvertisingSection;
